import React, { useEffect, useState } from 'react';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar, PageShellLoading } from './MyAccountLoadingStates';

interface EndpointResponse {
    success: boolean;
    data?: {
        html?: string;
        endpoint?: string;
    };
    message?: string;
}

interface MyAccountLegacyEndpointProps {
    endpoint?: string;
    full_page_url?: string;
}

const MyAccountLegacyEndpoint: React.FC<MyAccountLegacyEndpointProps> = ({
    endpoint = '',
    full_page_url = '',
}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [html, setHtml] = useState('');
    const safeEndpoint = String(endpoint || '').trim();
    const fullPageUrl = String(full_page_url || '').trim() || (safeEndpoint ? `/my-account/${safeEndpoint}/` : '/my-account/');

    useEffect(() => {
        if (!safeEndpoint) {
            setError('Missing endpoint.');
            setLoading(false);
            return;
        }

        const apiBase = (window as any).wekaApiSettings?.api_endpoint_base || 'weka/v1';
        const nonce = (window as any).wekaApiSettings?.nonce || '';
        const url = `/wp-json/${apiBase}/my-account/endpoint-content?endpoint=${encodeURIComponent(safeEndpoint)}`;

        setLoading(true);
        setError(null);
        fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': nonce,
            },
        })
            .then(async (response) => {
                const payload: EndpointResponse = await response.json().catch(() => ({} as EndpointResponse));
                if (!response.ok || payload.success === false) {
                    throw new Error(payload?.message || 'Failed to load page content.');
                }
                setHtml((payload?.data?.html || '').toString());
            })
            .catch((err: any) => {
                setError(err?.message || 'Failed to load page content.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [safeEndpoint]);

    if (loading) {
        return (
            <PageShellLoading titleWidth="24%" subtitleWidth="42%">
                <MyAccountCard
                    variant="page"
                    slug="legacy-endpoint-loading"
                    className="weka-my-account-page__section-card"
                    wrapContent={false}
                >
                        <LoadingBar height="18px" width="84%" style={{ marginBottom: '12px' }} />
                        <LoadingBar height="18px" width="72%" style={{ marginBottom: '12px' }} />
                        <LoadingBar height="18px" width="78%" style={{ marginBottom: '12px' }} />
                        <LoadingBar height="18px" width="64%" />
                </MyAccountCard>
            </PageShellLoading>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell>
                <div className="woocommerce-error" role="alert">
                    {error}
                </div>
                <p>
                    <a href={fullPageUrl} className="button">
                        Open Full Page
                    </a>
                </p>
            </MyAccountPageShell>
        );
    }

    return (
        <MyAccountPageShell contentClassName="weka-my-account-page__legacy-content">
            <div dangerouslySetInnerHTML={{ __html: html }} />
        </MyAccountPageShell>
    );
};

export default MyAccountLegacyEndpoint;
